import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Plus,
  Trash2,
  ListChecks,
  RefreshCw,
  ArrowDownToLine,
  ArrowUpFromLine,
  Truck,
  Ship,
  ArrowRightLeft,
} from "lucide-react";


export default function TaskTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    label: "",
    description: "",
    direction: "all",
  });
  const [submitting, setSubmitting] = useState(false);
  const { getAuthHeaders, user } = useAuth();

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/pf-tasks/templates`, {
        headers: getAuthHeaders(),
      });
      setTemplates(response.data.templates || []);
      setDefaults(response.data.defaults || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load task templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleAddTemplate = async () => {
    if (!newTemplate.label.trim()) {
      toast.error("Please enter a task name");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        label: newTemplate.label.trim(),
        description: newTemplate.description.trim(),
        direction: newTemplate.direction === "all" ? null : newTemplate.direction,
      };

      await axios.post(`${API_URL}/pf-tasks/templates`, payload, {
        headers: getAuthHeaders(),
      });

      toast.success(`Task template "${newTemplate.label}" added`);
      setShowAddModal(false);
      setNewTemplate({ label: "", description: "", direction: "all" });
      fetchTemplates();
    } catch (error) {
      console.error("Error adding template:", error);
      toast.error("Failed to add task template");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Delete task template "${template.label}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/pf-tasks/templates/${template.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(`Task template deleted`);
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(error.response?.data?.detail || "Failed to delete template");
    }
  };

  const getDirectionIcon = (direction) => {
    switch (direction) {
      case "import":
        return <ArrowDownToLine className="h-4 w-4 text-blue-500" />;
      case "export":
        return <ArrowUpFromLine className="h-4 w-4 text-green-500" />;
      case "cross_trade":
        return <ArrowRightLeft className="h-4 w-4 text-purple-500" />;
      default:
        return <ListChecks className="h-4 w-4 text-slate-500" />;
    }
  };

  const getDirectionLabel = (direction) => {
    switch (direction) {
      case "import":
        return "Import";
      case "export":
        return "Export";
      case "cross_trade":
        return "Cross Trade";
      default:
        return "All Directions";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navbar />
      <main className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ListChecks className="h-7 w-7 text-blue-600" />
              Task Templates
            </h1>
            <p className="text-slate-600 mt-1">
              Manage company-wide quick-add task templates for Position Folders
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchTemplates}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Task Template
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ListChecks className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{templates.length}</p>
                <p className="text-sm text-slate-500">Custom Templates</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <ListChecks className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{defaults.length}</p>
                <p className="text-sm text-slate-500">System Defaults</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <ListChecks className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{templates.length + defaults.length}</p>
                <p className="text-sm text-slate-500">Total Available</p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Templates Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <Plus className="h-4 w-4 text-blue-500" />
              Custom Task Templates
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              These tasks appear as quick-add buttons for all staff in Position Folder task checklists
            </p>
          </div>
          <div className="p-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <ListChecks className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No custom task templates yet</p>
                <p className="text-sm">Click "Add Task Template" to create one</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        {getDirectionIcon(template.direction)}
                        <span className="font-medium text-slate-800">{template.label}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteTemplate(template)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {template.description && (
                      <p className="text-xs text-slate-500 mt-1 ml-6">{template.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 ml-6">
                      <span className="text-xs px-2 py-0.5 bg-slate-200 rounded text-slate-600">
                        {getDirectionLabel(template.direction)}
                      </span>
                      {template.created_by && (
                        <span className="text-xs text-slate-400">by {template.created_by}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* System Defaults Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-slate-500" />
              System Default Tasks
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Built-in task templates (cannot be deleted)
            </p>
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              {defaults.map((task, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1.5 bg-slate-100 rounded-full text-sm text-slate-700 border border-slate-200"
                >
                  + {task.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Add Template Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-blue-500" />
                Add Task Template
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="task-name">Task Name *</Label>
                <Input
                  id="task-name"
                  placeholder="e.g., Check customs status"
                  value={newTemplate.label}
                  onChange={(e) => setNewTemplate({ ...newTemplate, label: e.target.value })}
                  data-testid="task-template-name"
                />
              </div>
              <div>
                <Label htmlFor="task-description">Description (optional)</Label>
                <Input
                  id="task-description"
                  placeholder="Brief description of this task"
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="task-direction">Applies To</Label>
                <Select
                  value={newTemplate.direction}
                  onValueChange={(val) => setNewTemplate({ ...newTemplate, direction: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Directions</SelectItem>
                    <SelectItem value="import">Import Only</SelectItem>
                    <SelectItem value="export">Export Only</SelectItem>
                    <SelectItem value="cross_trade">Cross Trade Only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Choose which shipment types this task applies to
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddTemplate} disabled={submitting}>
                {submitting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Template
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

export { TaskTemplatesPage };
